import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  InputNumber,
  Typography,
  Popconfirm,
  message,
} from "antd";
import moment from "moment";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import FinanceService from "Services/FinanceService";
import {
  ExpenseManagemnetStatusEnum,
  ExpenseManagemnetStatusLabel,
} from "Constants/Enums/expenseManagmentEnum";

const { Text } = Typography;

const money = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const empName = (r) =>
  r.employee?.fullName ||
  [r.employee?.firstName, r.employee?.lastName].filter(Boolean).join(" ") ||
  r.employeeName ||
  "-";
const deptName = (r) => r.department?.name || r.department?.departmentName || r.departmentName || "-";

// The expense list returns the id as `id` (the handoff doc called it
// expenseManagementId, but the API returns `id`, same as the supervisor list).
const rowId = (r) => r.id ?? r.expenseManagementId;

const STATUS_COLOR = {
  [ExpenseManagemnetStatusEnum.Pending]: "gold",
  [ExpenseManagemnetStatusEnum.Inprocess]: "cyan",
  [ExpenseManagemnetStatusEnum.Approved]: "success",
  [ExpenseManagemnetStatusEnum.Paid]: "blue",
  [ExpenseManagemnetStatusEnum.Rejected]: "error",
};
const statusTag = (s) => (
  <Tag color={STATUS_COLOR[s] || "default"}>{ExpenseManagemnetStatusLabel[s] || "Unknown"}</Tag>
);

// Finance expense work queue (Points 7 & 8). Finance approves Inprocess
// expenses, marks Approved expenses as Paid, or rejects, and may confirm or
// adjust the final amount when approving / marking paid.
const FinanceExpenseQueue = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  // Approve / Mark-Paid modal (captures finalAmount).
  const [modal, setModal] = useState(null); // { record, mode: "approve" | "paid" }
  const [finalAmount, setFinalAmount] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await FinanceService.getQueue();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load finance queue", e);
      message.error("Failed to load the expense queue");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openModal = (record, mode) => {
    setModal({ record, mode });
    setFinalAmount(record.finalAmount ?? record.charges ?? null);
  };

  const submitModal = async () => {
    if (!modal) return;
    try {
      setActing(true);
      const status =
        modal.mode === "approve"
          ? ExpenseManagemnetStatusEnum.Approved
          : ExpenseManagemnetStatusEnum.Paid;
      await FinanceService.updateStatus({
        expenseManagementId: rowId(modal.record),
        status,
        finalAmount: finalAmount === null || finalAmount === "" ? null : Number(finalAmount),
      });
      message.success(modal.mode === "approve" ? "Expense approved" : "Expense marked as paid");
      setModal(null);
      setFinalAmount(null);
      load();
    } catch (e) {
      message.error(e.response?.data?.details || e.response?.data?.title || "Action failed");
    } finally {
      setActing(false);
    }
  };

  const rejectExpense = async (record) => {
    try {
      setActing(true);
      await FinanceService.updateStatus({
        expenseManagementId: rowId(record),
        status: ExpenseManagemnetStatusEnum.Rejected,
        finalAmount: null,
      });
      message.success("Expense rejected");
      load();
    } catch (e) {
      message.error(e.response?.data?.details || "Reject failed");
    } finally {
      setActing(false);
    }
  };

  const columns = [
    { title: "Employee", key: "emp", render: (_, r) => empName(r) },
    { title: "Department", key: "dept", render: (_, r) => deptName(r) },
    { title: "Charges", key: "charges", render: (_, r) => money(r.charges, "USD") },
    { title: "Final Amount", key: "final", render: (_, r) => money(r.finalAmount, "USD") },
    { title: "Status", key: "status", render: (_, r) => statusTag(r.status) },
    { title: "Approved", key: "approved", render: (_, r) => fmtDate(r.approvedDate) },
    {
      title: "Action",
      key: "action",
      render: (_, r) => {
        if (r.status === ExpenseManagemnetStatusEnum.Inprocess) {
          return (
            <Space>
              <Button type="link" onClick={() => openModal(r, "approve")}>
                Approve
              </Button>
              <Popconfirm
                title="Reject this expense?"
                okText="Reject"
                okButtonProps={{ danger: true, loading: acting }}
                onConfirm={() => rejectExpense(r)}
              >
                <Button type="link" danger>
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        if (r.status === ExpenseManagemnetStatusEnum.Approved) {
          return (
            <Button type="link" onClick={() => openModal(r, "paid")}>
              Mark Paid
            </Button>
          );
        }
        return "-";
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card title="Expense Work Queue" className="card-professional" style={{ margin: 16 }}>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Expenses verified by supervisors (Inprocess) and approved expenses awaiting payment.
        </Text>
        <Table
          className="custom-table"
          rowKey={(r) => rowId(r)}
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No expenses in the queue" }}
        />
      </Card>

      <Modal
        title={modal?.mode === "paid" ? "Mark as Paid" : "Approve Expense"}
        open={modal !== null}
        onCancel={() => {
          setModal(null);
          setFinalAmount(null);
        }}
        onOk={submitModal}
        okText={modal?.mode === "paid" ? "Mark Paid" : "Approve"}
        okButtonProps={{ loading: acting }}
      >
        <Text>Confirm or adjust the final amount:</Text>
        <InputNumber
          min={0}
          value={finalAmount}
          onChange={setFinalAmount}
          style={{ width: "100%", marginTop: 8 }}
        />
      </Modal>
    </DashboardLayout>
  );
};

export default FinanceExpenseQueue;
